// ─── Types ───────────────────────────────────────────────────────────────────

export type JobStatus =
  | "Draft"
  | "Pending Approval"
  | "Approved"
  | "Active"
  | "On Hold"
  | "Filled"
  | "Closed";
export type CandidateStage =
  | "Applied"
  | "Screened"
  | "Shortlisted"
  | "Interview"
  | "Offered"
  | "Hired"
  | "Rejected";
export type InterviewRound =
  | "Round 1"
  | "Round 2"
  | "Technical"
  | "HR"
  | "Final";
export type InterviewStatus =
  | "Scheduled"
  | "Completed"
  | "Cancelled"
  | "No Show";

export interface Job {
  id: string;
  title: string;
  jobId: string;
  department: string;
  location: string;
  jobType: "Remote" | "On-site" | "Hybrid";
  experience: string;
  skills: string[];
  budget: string;
  hiringManager: string;
  status: JobStatus;
  approvalStage:
    | "Pending"
    | "L1 Approved"
    | "L2 Approved"
    | "Final Approved"
    | "Rejected";
  vacancies: number;
  applications: number;
  shortlisted: number;
  description: string;
  createdAt: string;
  expiryDate: string;
}

export interface Candidate {
  id: string;
  name: string;
  email: string;
  phone: string;
  location: string;
  jobId: string;
  jobTitle: string;
  experience: string;
  skills: string[];
  education: string;
  previousCompany: string;
  stage: CandidateStage;
  matchScore: number;
  aiSummary: string;
  redFlags: string[];
  avatar: string;
  appliedDate: string;
  resumeUrl?: string;
}

export interface Interview {
  id: string;
  candidateId: string;
  candidateName: string;
  candidateAvatar: string;
  jobId: string;
  jobTitle: string;
  round: InterviewRound;
  status: InterviewStatus;
  interviewerId: string;
  interviewerName: string;
  date: string;
  time: string;
  duration: string;
  feedback?: {
    rating: number;
    strengths: string[];
    weaknesses: string[];
    notes: string;
    recommendation: "Hire" | "Reject" | "Next Round" | "Hold";
  };
  aiQuestions?: string[];
}

export interface Notification {
  id: string;
  type: "interview" | "status" | "offer" | "application";
  title: string;
  description: string;
  time: string;
  read: boolean;
}

// ─── Mock Jobs ───────────────────────────────────────────────────────────────

export const mockJobs: Job[] = [
  {
    id: "j1",
    title: "Senior UX Designer",
    jobId: "JOB-001",
    department: "Design",
    location: "Bengaluru",
    jobType: "Hybrid",
    experience: "5+ Years",
    skills: ["Figma", "User Research", "Prototyping", "Design Systems"],
    budget: "₹25-35 LPA",
    hiringManager: "Admirra John",
    status: "Active",
    approvalStage: "Final Approved",
    vacancies: 3,
    applications: 45,
    shortlisted: 8,
    description:
      "We are looking for a Senior UX Designer to lead product design initiatives...",
    createdAt: "2026-02-15",
    expiryDate: "2026-04-15",
  },
  {
    id: "j2",
    title: "Growth Marketing Manager",
    jobId: "JOB-002",
    department: "Marketing",
    location: "Mumbai",
    jobType: "Remote",
    experience: "4+ Years",
    skills: ["SEO", "Analytics", "Content Strategy", "Performance Marketing"],
    budget: "₹18-25 LPA",
    hiringManager: "Priya Kapoor",
    status: "Active",
    approvalStage: "Final Approved",
    vacancies: 2,
    applications: 38,
    shortlisted: 5,
    description: "Drive growth through data-driven marketing strategies...",
    createdAt: "2026-02-20",
    expiryDate: "2026-04-20",
  },
  {
    id: "j3",
    title: "Financial Analyst",
    jobId: "JOB-003",
    department: "Finance",
    location: "Delhi",
    jobType: "On-site",
    experience: "2+ Years",
    skills: ["Excel", "SQL", "Financial Modeling", "Tableau"],
    budget: "₹12-18 LPA",
    hiringManager: "Vikram Singh",
    status: "Active",
    approvalStage: "Final Approved",
    vacancies: 4,
    applications: 52,
    shortlisted: 10,
    description: "Analyze financial data and build forecasting models...",
    createdAt: "2026-02-25",
    expiryDate: "2026-04-25",
  },
  {
    id: "j4",
    title: "Backend Engineer",
    jobId: "JOB-004",
    department: "Engineering",
    location: "Bengaluru",
    jobType: "Hybrid",
    experience: "3+ Years",
    skills: ["Python", "Django", "PostgreSQL", "Redis", "Docker"],
    budget: "₹20-30 LPA",
    hiringManager: "Rahul Mehta",
    status: "Pending Approval",
    approvalStage: "L1 Approved",
    vacancies: 2,
    applications: 0,
    shortlisted: 0,
    description: "Build scalable backend services and APIs...",
    createdAt: "2026-03-10",
    expiryDate: "2026-05-10",
  },
  {
    id: "j5",
    title: "Product Manager",
    jobId: "JOB-005",
    department: "Product",
    location: "Hyderabad",
    jobType: "Remote",
    experience: "6+ Years",
    skills: [
      "Product Strategy",
      "Agile",
      "Data Analysis",
      "Stakeholder Management",
    ],
    budget: "₹30-45 LPA",
    hiringManager: "Admirra John",
    status: "Draft",
    approvalStage: "Pending",
    vacancies: 1,
    applications: 0,
    shortlisted: 0,
    description: "Lead product vision and roadmap for core platform...",
    createdAt: "2026-03-15",
    expiryDate: "2026-05-15",
  },
  {
    id: "j6",
    title: "DevOps Engineer",
    jobId: "JOB-006",
    department: "Engineering",
    location: "Pune",
    jobType: "Remote",
    experience: "4+ Years",
    skills: ["AWS", "Kubernetes", "CI/CD", "Terraform", "Linux"],
    budget: "₹22-32 LPA",
    hiringManager: "Rahul Mehta",
    status: "On Hold",
    approvalStage: "Final Approved",
    vacancies: 1,
    applications: 22,
    shortlisted: 3,
    description: "Manage cloud infrastructure and deployment pipelines...",
    createdAt: "2026-01-20",
    expiryDate: "2026-03-20",
  },
  {
    id: "j7",
    title: "Data Scientist",
    jobId: "JOB-007",
    department: "Engineering",
    location: "Bengaluru",
    jobType: "Hybrid",
    experience: "3+ Years",
    skills: ["Python", "ML", "TensorFlow", "Statistics", "SQL"],
    budget: "₹25-40 LPA",
    hiringManager: "Priya Kapoor",
    status: "Filled",
    approvalStage: "Final Approved",
    vacancies: 2,
    applications: 67,
    shortlisted: 12,
    description: "Build ML models for predictive analytics...",
    createdAt: "2026-01-10",
    expiryDate: "2026-03-10",
  },
  {
    id: "j8",
    title: "HR Business Partner",
    jobId: "JOB-008",
    department: "HR",
    location: "Mumbai",
    jobType: "On-site",
    experience: "5+ Years",
    skills: [
      "Employee Relations",
      "Performance Management",
      "HRIS",
      "Compliance",
    ],
    budget: "₹15-22 LPA",
    hiringManager: "Admirra John",
    status: "Closed",
    approvalStage: "Final Approved",
    vacancies: 1,
    applications: 30,
    shortlisted: 6,
    description: "Strategic HR partner for business units...",
    createdAt: "2026-01-05",
    expiryDate: "2026-02-28",
  },
];

// ─── Mock Candidates ─────────────────────────────────────────────────────────

export const mockCandidates: Candidate[] = [
  {
    id: "c1",
    name: "Priya Sharma",
    email: "priya.s@gmail.com",
    phone: "+91 98765 43210",
    location: "Mumbai",
    jobId: "j1",
    jobTitle: "Senior UX Designer",
    experience: "5 Years",
    skills: ["Figma", "React", "User Research", "Prototyping"],
    education: "M.Des, IIT Bombay",
    previousCompany: "Flipkart",
    stage: "Shortlisted",
    matchScore: 92,
    aiSummary:
      "Excellent match with 5+ years in UX design. Strong portfolio with design system experience. Great cultural fit.",
    redFlags: [],
    avatar: "PS",
    appliedDate: "2026-02-28",
  },
  {
    id: "c2",
    name: "Rahul Verma",
    email: "rahul.v@gmail.com",
    phone: "+91 87654 32109",
    location: "Delhi",
    jobId: "j2",
    jobTitle: "Growth Marketing Manager",
    experience: "4 Years",
    skills: ["SEO", "Analytics", "Content Strategy"],
    education: "MBA, ISB Hyderabad",
    previousCompany: "Razorpay",
    stage: "Interview",
    matchScore: 85,
    aiSummary: "Strong growth marketing background with B2B SaaS experience.",
    redFlags: ["Frequent job changes (3 companies in 4 years)"],
    avatar: "RV",
    appliedDate: "2026-03-01",
  },
  {
    id: "c3",
    name: "Anita Desai",
    email: "anita.d@outlook.com",
    phone: "+91 76543 21098",
    location: "Bengaluru",
    jobId: "j3",
    jobTitle: "Financial Analyst",
    experience: "3 Years",
    skills: ["Excel", "SQL", "Financial Modeling", "Tableau"],
    education: "CA, ICAI",
    previousCompany: "Deloitte",
    stage: "Shortlisted",
    matchScore: 95,
    aiSummary:
      "Top-tier candidate with CA qualification and Big 4 experience. Perfect skill match.",
    redFlags: [],
    avatar: "AD",
    appliedDate: "2026-03-02",
  },
  {
    id: "c4",
    name: "Vikram Singh",
    email: "vikram.s@yahoo.com",
    phone: "+91 65432 10987",
    location: "Pune",
    jobId: "j1",
    jobTitle: "Senior UX Designer",
    experience: "6 Years",
    skills: ["Sketch", "Prototyping", "Interaction Design", "Accessibility"],
    education: "B.Des, NID Ahmedabad",
    previousCompany: "Swiggy",
    stage: "Offered",
    matchScore: 88,
    aiSummary:
      "Creative UX professional with excellent interaction design skills.",
    redFlags: ["Salary expectations 20% above budget"],
    avatar: "VS",
    appliedDate: "2026-02-25",
  },
  {
    id: "c5",
    name: "Sneha Patel",
    email: "sneha.p@gmail.com",
    phone: "+91 54321 09876",
    location: "Ahmedabad",
    jobId: "j7",
    jobTitle: "Data Scientist",
    experience: "7 Years",
    skills: ["Python", "ML", "TensorFlow", "Statistics"],
    education: "PhD, IISc Bangalore",
    previousCompany: "Google",
    stage: "Hired",
    matchScore: 97,
    aiSummary:
      "Exceptional candidate with PhD and Google experience. Published researcher.",
    redFlags: [],
    avatar: "SP",
    appliedDate: "2026-01-15",
  },
  {
    id: "c6",
    name: "Arjun Nair",
    email: "arjun.n@gmail.com",
    phone: "+91 43210 98765",
    location: "Chennai",
    jobId: "j1",
    jobTitle: "Senior UX Designer",
    experience: "8 Years",
    skills: ["UI Design", "Branding", "Design Systems", "Motion"],
    education: "MFA, Srishti Institute",
    previousCompany: "Zoho",
    stage: "Interview",
    matchScore: 90,
    aiSummary: "Award-winning designer with strong leadership experience.",
    redFlags: [],
    avatar: "AN",
    appliedDate: "2026-03-03",
  },
  {
    id: "c7",
    name: "Meera Joshi",
    email: "meera.j@hotmail.com",
    phone: "+91 32109 87654",
    location: "Hyderabad",
    jobId: "j2",
    jobTitle: "Growth Marketing Manager",
    experience: "4 Years",
    skills: ["Performance Marketing", "SEO", "Analytics"],
    education: "MBA, MICA",
    previousCompany: "Infosys",
    stage: "Applied",
    matchScore: 72,
    aiSummary: "Good foundation but limited SaaS experience.",
    redFlags: ["No B2B SaaS experience", "Skills gap in data analytics"],
    avatar: "MJ",
    appliedDate: "2026-03-06",
  },
  {
    id: "c8",
    name: "Karan Gupta",
    email: "karan.g@gmail.com",
    phone: "+91 21098 76543",
    location: "Noida",
    jobId: "j4",
    jobTitle: "Backend Engineer",
    experience: "5 Years",
    skills: ["Python", "Django", "PostgreSQL", "Docker"],
    education: "B.Tech, IIT Delhi",
    previousCompany: "Atlassian",
    stage: "Applied",
    matchScore: 91,
    aiSummary: "Excellent backend engineer with FAANG-level experience.",
    redFlags: [],
    avatar: "KG",
    appliedDate: "2026-03-12",
  },
  {
    id: "c9",
    name: "Deepa Raman",
    email: "deepa.r@gmail.com",
    phone: "+91 98712 34567",
    location: "Bengaluru",
    jobId: "j3",
    jobTitle: "Financial Analyst",
    experience: "2 Years",
    skills: ["Excel", "SQL", "Tableau"],
    education: "B.Com, Christ University",
    previousCompany: "EY",
    stage: "Screened",
    matchScore: 78,
    aiSummary: "Promising junior candidate with Big 4 experience.",
    redFlags: ["Below minimum experience requirement"],
    avatar: "DR",
    appliedDate: "2026-03-04",
  },
  {
    id: "c10",
    name: "Amit Saxena",
    email: "amit.s@gmail.com",
    phone: "+91 87612 45678",
    location: "Mumbai",
    jobId: "j1",
    jobTitle: "Senior UX Designer",
    experience: "4 Years",
    skills: ["Figma", "User Research"],
    education: "B.Des, MIT Pune",
    previousCompany: "Ola",
    stage: "Rejected",
    matchScore: 55,
    aiSummary: "Below expectations for senior role. Portfolio lacks depth.",
    redFlags: ["Insufficient experience for senior role", "Weak portfolio"],
    avatar: "AS",
    appliedDate: "2026-02-20",
  },
  {
    id: "c11",
    name: "Neha Kulkarni",
    email: "neha.k@gmail.com",
    phone: "+91 76512 89012",
    location: "Pune",
    jobId: "j7",
    jobTitle: "Data Scientist",
    experience: "3 Years",
    skills: ["Python", "ML", "SQL", "Statistics"],
    education: "M.Tech, IIT Kanpur",
    previousCompany: "Amazon",
    stage: "Hired",
    matchScore: 93,
    aiSummary: "Strong ML engineer with production deployment experience.",
    redFlags: [],
    avatar: "NK",
    appliedDate: "2026-01-18",
  },
  {
    id: "c12",
    name: "Rohan Deshmukh",
    email: "rohan.d@gmail.com",
    phone: "+91 65412 78901",
    location: "Bengaluru",
    jobId: "j3",
    jobTitle: "Financial Analyst",
    experience: "3 Years",
    skills: ["Financial Modeling", "Excel", "Python", "SQL"],
    education: "MBA Finance, IIM Kozhikode",
    previousCompany: "KPMG",
    stage: "Interview",
    matchScore: 87,
    aiSummary: "Strong analytical skills with consulting background.",
    redFlags: [],
    avatar: "RD",
    appliedDate: "2026-03-01",
  },
];

// ─── Mock Interviews ─────────────────────────────────────────────────────────

export const mockInterviews: Interview[] = [
  {
    id: "i1",
    candidateId: "c2",
    candidateName: "Rahul Verma",
    candidateAvatar: "RV",
    jobId: "j2",
    jobTitle: "Growth Marketing Manager",
    round: "Round 1",
    status: "Completed",
    interviewerId: "u3",
    interviewerName: "Rahul Mehta",
    date: "2026-03-20",
    time: "10:00 AM",
    duration: "45 min",
    feedback: {
      rating: 4.2,
      strengths: [
        "Strong analytical thinking",
        "Good communication",
        "Relevant experience",
      ],
      weaknesses: ["Limited SaaS knowledge"],
      notes: "Good candidate overall. Needs more SaaS exposure.",
      recommendation: "Next Round",
    },
    aiQuestions: [
      "Describe a growth campaign that significantly improved user acquisition.",
      "How do you prioritize between paid and organic growth channels?",
      "Walk us through your approach to A/B testing.",
      "How do you measure the success of a marketing campaign?",
      "Tell us about a time you had to pivot a strategy based on data.",
    ],
  },
  {
    id: "i2",
    candidateId: "c6",
    candidateName: "Arjun Nair",
    candidateAvatar: "AN",
    jobId: "j1",
    jobTitle: "Senior UX Designer",
    round: "Technical",
    status: "Scheduled",
    interviewerId: "u3",
    interviewerName: "Rahul Mehta",
    date: "2026-03-28",
    time: "2:00 PM",
    duration: "60 min",
    aiQuestions: [
      "Walk us through your design process for a complex enterprise feature.",
      "How do you ensure design system consistency across teams?",
      "Describe your experience with accessibility standards.",
      "How do you handle design critiques and stakeholder feedback?",
      "Show us a case study where you improved a key metric through design.",
    ],
  },
  {
    id: "i3",
    candidateId: "c12",
    candidateName: "Rohan Deshmukh",
    candidateAvatar: "RD",
    jobId: "j3",
    jobTitle: "Financial Analyst",
    round: "Round 1",
    status: "Scheduled",
    interviewerId: "u2",
    interviewerName: "Priya Kapoor",
    date: "2026-03-27",
    time: "11:00 AM",
    duration: "45 min",
    aiQuestions: [
      "Explain your approach to financial modeling.",
      "How do you handle discrepancies in financial data?",
      "Walk us through a variance analysis you conducted.",
      "How do you communicate financial insights to non-finance stakeholders?",
      "Describe your experience with Tableau dashboards.",
    ],
  },
  {
    id: "i4",
    candidateId: "c1",
    candidateName: "Priya Sharma",
    candidateAvatar: "PS",
    jobId: "j1",
    jobTitle: "Senior UX Designer",
    round: "HR",
    status: "Scheduled",
    interviewerId: "u2",
    interviewerName: "Priya Kapoor",
    date: "2026-03-29",
    time: "3:00 PM",
    duration: "30 min",
  },
  {
    id: "i5",
    candidateId: "c2",
    candidateName: "Rahul Verma",
    candidateAvatar: "RV",
    jobId: "j2",
    jobTitle: "Growth Marketing Manager",
    round: "Round 2",
    status: "Scheduled",
    interviewerId: "u1",
    interviewerName: "Admirra John",
    date: "2026-03-30",
    time: "10:00 AM",
    duration: "45 min",
  },
];

// ─── Mock Notifications ──────────────────────────────────────────────────────

export const mockNotifications: Notification[] = [
  {
    id: "n1",
    type: "application",
    title: "New application received",
    description: "Karan Gupta applied for Backend Engineer",
    time: "2m ago",
    read: false,
  },
  {
    id: "n2",
    type: "interview",
    title: "Interview scheduled",
    description: "Arjun Nair — Technical round at 2:00 PM",
    time: "15m ago",
    read: false,
  },
  {
    id: "n3",
    type: "status",
    title: "Job posting expiring",
    description: "DevOps Engineer — expires in 3 days",
    time: "1h ago",
    read: false,
  },
  {
    id: "n4",
    type: "offer",
    title: "Offer accepted",
    description: "Vikram Singh accepted Senior UX Designer offer",
    time: "6h ago",
    read: true,
  },
  {
    id: "n5",
    type: "application",
    title: "AI screening complete",
    description: "12 candidates scored for Financial Analyst",
    time: "1d ago",
    read: true,
  },
];

// ─── Pipeline Stats ──────────────────────────────────────────────────────────

export const pipelineStages: { stage: CandidateStage; color: string }[] = [
  { stage: "Applied", color: "hsl(200 80% 35%)" },
  { stage: "Screened", color: "hsl(199 89% 48%)" },
  { stage: "Shortlisted", color: "hsl(38 92% 50%)" },
  { stage: "Interview", color: "hsl(170 60% 42%)" },
  { stage: "Offered", color: "hsl(160 84% 39%)" },
  { stage: "Hired", color: "hsl(130 60% 40%)" },
  { stage: "Rejected", color: "hsl(350 80% 55%)" },
];

export function getCandidatesByStage(stage: CandidateStage) {
  return mockCandidates.filter(c => c.stage === stage);
}

export function getCandidatesForJob(jobId: string) {
  return mockCandidates.filter(c => c.jobId === jobId);
}

export function getInterviewsForCandidate(candidateId: string) {
  return mockInterviews.filter(i => i.candidateId === candidateId);
}
